import React, {useState, useEffect} from 'react';
import {Box} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import HeaderSection from './Header';
import DetailItem, {RowType} from './Item';
import SummarySection, {ISummarySectionProps} from './Summary';
import InternalTemplate from '../../../../../../components/templates/internal';
import {StackParamList} from '../../../../../../navigators';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../components/buttons';
import {useContextState as useSubClientContextState} from '../../../../../../context/sub-client';
import IInvoiceFeeSummary from '../../../../../../interfaces/dtos/invoice-fee-summary.interface';
import IInvoiceFee from '../../../../../../interfaces/dtos/invoice-fee.interface';

type ScreenProps = NativeStackScreenProps<StackParamList, 'InvoiceFeesScreen'>;
const defaultAmount = {
  foreign: 0,
  local: 0,
} as ISummarySectionProps;

const InvoiceFeesScreen = ({navigation, route}: ScreenProps) => {
  const [detail, setDetail] = useState(null as IInvoiceFeeSummary | null);
  const {setInvoiceFeeSummary, setInvoiceFeeListToPay} =
    useSubClientContextState();
  const [selection, setSelection] = useState([] as number[]);
  const [amount, setAmount] = useState(defaultAmount);

  useEffect(() => {
    setDetail(route.params);
  }, [route.params]);

  const handleGoBack = () => {
    navigation.pop(1);
  };

  const assignInvoiceFees = () => {
    setInvoiceFeeSummary(detail);
    setInvoiceFeeListToPay(selection.map(position => detail!.fees[position]));
  };

  const handleAssignInvoiceFees = async () => {
    if (detail) {
      assignInvoiceFees();
      navigation.navigate('FeePaymentOptionScreen');
    }
  };

  function isOdd(num: number) {
    return num % 2;
  }

  const handleOnPress = (position: number) => {
    let list = null;
    if (selection.includes(position)) {
      list = selection.filter(item => item !== position);
    } else {
      list = [...selection, position];
    }
    setSelection(list);

    const newAmount = {...defaultAmount};

    list.forEach(pos => {
      const fee = detail?.fees[pos];
      newAmount.local += fee?.amount.local ? fee?.amount.local : 0;
      newAmount.foreign += fee?.amount.foreign ? fee?.amount.foreign : 0;
    });

    setAmount(newAmount);
  };

  const getRowType = (index: number): RowType => {
    if (selection.includes(index)) {
      return 'selected';
    }
    return isOdd(index) ? 'odd' : 'even';
  };

  const Content = () => {
    if (detail && detail.fees) {
      return (
        <>
          {detail?.fees.map((fee: IInvoiceFee, index: number) => {
            return (
              <DetailItem
                key={`detail-item-{${index}}`}
                onPress={() => handleOnPress(index)}
                rowType={getRowType(index)}
                fee={fee}
              />
            );
          })}
        </>
      );
    }

    return <></>;
  };

  if (detail == null) {
    return <></>;
  }

  const handleGoFeeDetail = () => {
    if (detail) {
      assignInvoiceFees();
      navigation.navigate('FeePaymentDetailScreen');
    }
  };

  return (
    <InternalTemplate
      title={'screen-balance-detail-title'}
      contentPaddingX="0"
      backButton={{
        label: 'go-back-screen',
        onPress: () => handleGoBack(),
        usage: 'external',
      }}>
      <>
        <HeaderSection detail={detail} />
        <Content />

        {selection.length > 0 && (
          <>
            <SummarySection foreign={amount.foreign} local={amount.local} />
            <Box paddingX="10%" mt={10} minHeight={150}>
              <Box mb={2}>
                <SecondaryButton
                  label="invoice-fees-detail-button"
                  onPress={() => handleGoFeeDetail()}
                />
              </Box>
              <PrimaryButton
                label="invoice-fees-action-button"
                onPress={() => handleAssignInvoiceFees()}
              />
            </Box>
          </>
        )}
      </>
    </InternalTemplate>
  );
};

export default InvoiceFeesScreen;
